// src/services/digitalJobService.js
//
// Job Produksi Digital (Toko Digital) report.
//
// Source: sursys backend `/datadigi/invoice-details` — line-item invoice data
// for the digital store. Each line carries a `strukno` (nomor struk / invoice)
// and a `remarks` whose first token is the digital job number (prefix "D",
// e.g. "D2611241 BANNER PANCA RUN ...").
//
// Goal of the page that consumes this:
//   1. Lihat seluruh job produksi digital (nomor depan "D") pada rentang tanggal.
//   2. Cari nomor job yang BELUM punya nomor struk.
//
// Because `invoice-details` only returns rows that already have a struk, a job
// "tanpa struk" is detected via SEQUENCE-GAP analysis: job numbers are issued
// sequentially (D + integer), so any integer missing between the smallest and
// largest job number seen in the period is a job that was issued a number but
// never appeared on a struk (belum tertagih / dibatalkan / terlewat).

import axios from 'axios';
import { fetchDigitalJobs } from './gapfamilyAPI';

// Dedicated client for the digital-job report.
//
// The shared `surroundingSys` client defaults to http://localhost:3044/api when
// VITE_SURSYS_URL is unset, which 404s in dev. The `/datadigi/*` endpoints live
// on the public sursys host, so default there and still allow an env override.
const digiApi = axios.create({
  baseURL:
    import.meta.env.VITE_SURSYS_URL ||
    'https://sursys.geloraaksarapratama.co.id/api',
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Pull the leading digital job number ("D" + digits) out of a remarks string.
// CATATAN: hanya "D" murni (job toko digital). Prefix "PD" = Product Development,
// BUKAN job digital, jadi sengaja tidak dicocokkan di sini.
export const extractJobNo = (remarks = '') => {
  const m = String(remarks).trim().match(/^(D\d+)/i);
  if (!m) return null;
  const jobNo = m[1].toUpperCase();
  return jobNo;
};

// Numeric part of a job number ("D2611241" -> 2611241), or null.
const jobSeq = (jobNo) => {
  if (!jobNo) return null;
  const n = parseInt(jobNo.slice(1), 10);
  return Number.isFinite(n) ? n : null;
};

// "D2611241 BANNER PANCA RUN 2026 : ANGGA" -> "BANNER PANCA RUN 2026 : ANGGA"
const stripJobNo = (remarks = '') =>
  String(remarks).trim().replace(/^D\d+\s*/i, '').trim();

// Wilayah / akun digital — DERIVED best-effort from `customername`.
//
// CATATAN: endpoint invoice-details TIDAK punya field `wilayahDigital`. Nilai di
// bawah diturunkan dari pola nama pelanggan (mis. "DKI R", "DKI D" -> "DKI",
// "MARKNAS" -> "Marketing Nasional"). Bila backend nanti menyediakan field
// wilayah asli, ganti fungsi ini agar membacanya langsung.
export const deriveWilayah = (customername = '') => {
  const c = String(customername).trim().toUpperCase();
  if (!c || c === '-CASH-' || c === 'CASH') return 'CASH / UMUM';
  if (/^DKI\b/.test(c)) return 'DKI';
  if (c.startsWith('MARKNAS')) return 'MARKETING NASIONAL';
  if (c.startsWith('MASTER DISKON')) return 'MASTER DISKON';
  // default: pakai kata pertama nama pelanggan sebagai pengelompokan kasar
  return c.split(/\s+/)[0];
};

/**
 * Fetch raw digital invoice line items for a date range.
 * @param {string} startDate YYYY-MM-DD
 * @param {string} endDate   YYYY-MM-DD
 * @returns {Promise<{count:number, data:Array}>}
 */
export const fetchDigitalInvoiceDetails = async (startDate, endDate) => {
  const { data } = await digiApi.get('/datadigi/invoice-details', {
    params: { startDate, endDate },
  });
  return { count: data?.count ?? (data?.data?.length || 0), data: data?.data || [] };
};

/**
 * Group raw line items into one row per digital job number.
 * @returns {{ jobs: Array, unmatched: Array }}
 *   jobs: [{ jobNo, seq, strukList[], strukno, strukdate, customer, category,
 *            judul, items[], qty, total, lineCount }]
 *   unmatched: line items whose remarks did not start with a "D" job number.
 */
export const groupByJob = (rows = []) => {
  const map = new Map();
  const unmatched = [];

  for (const r of rows) {
    const jobNo = extractJobNo(r.remarks);
    if (!jobNo) {
      unmatched.push(r);
      continue;
    }
    if (!map.has(jobNo)) {
      map.set(jobNo, {
        jobNo,
        seq: jobSeq(jobNo),
        strukSet: new Set(),
        strukdate: r.strukdate || '',
        customer: r.customername || '-',
        categorySet: new Set(),
        judul: stripJobNo(r.remarks),
        items: [],
        qty: 0,
        total: 0,
        lineCount: 0,
      });
    }
    const job = map.get(jobNo);
    if (r.strukno) job.strukSet.add(r.strukno);
    if (r.categorycode) job.categorySet.add(r.categorycode);
    if (r.itemname) job.items.push(r.itemname);
    job.qty += num(r.quantity);
    job.total += num(r.totalamount ?? r.total);
    job.lineCount += 1;
    // keep the earliest struk date
    if (r.strukdate && (!job.strukdate || r.strukdate < job.strukdate)) {
      job.strukdate = r.strukdate;
    }
  }

  const jobs = Array.from(map.values())
    .map((j) => ({
      jobNo: j.jobNo,
      seq: j.seq,
      strukList: Array.from(j.strukSet),
      strukno: Array.from(j.strukSet).join(', ') || '-',
      strukCount: j.strukSet.size,
      strukdate: j.strukdate,
      customer: j.customer,
      wilayah: deriveWilayah(j.customer),
      category: Array.from(j.categorySet).join(', ') || '-',
      categoryList: Array.from(j.categorySet),
      judul: j.judul,
      items: j.items,
      qty: j.qty,
      total: j.total,
      lineCount: j.lineCount,
      hasStruk: j.strukSet.size > 0,
    }))
    .sort((a, b) => (b.seq ?? 0) - (a.seq ?? 0));

  return { jobs, unmatched };
};

/**
 * Sequence-gap analysis: find job numbers that were issued (fall between the
 * min and max job number seen) but never appear on a struk in this period.
 *
 * Each missing job is returned WITH CONTEXT: the nearest existing job below
 * (`prev`) and above (`next`) it in the sequence, so the page can show "sedikit
 * detail" — pelanggan/wilayah/tanggal di sekitar nomor yang hilang.
 *
 * To avoid flooding over very wide ranges the list is capped to `maxList`
 * (newest first); `totalMissing` still reflects the true count.
 */
export const detectMissingStruk = (jobs = [], { maxSpan = 5000, maxList = 500 } = {}) => {
  const withSeq = jobs.filter((j) => Number.isFinite(j.seq)).sort((a, b) => a.seq - b.seq);
  if (withSeq.length < 2) {
    return { missing: [], totalMissing: 0, min: null, max: null, span: 0, capped: false };
  }
  const present = new Set(withSeq.map((j) => j.seq));
  const min = withSeq[0].seq;
  const max = withSeq[withSeq.length - 1].seq;
  const span = max - min;
  const lower = span > maxSpan ? max - maxSpan : min;

  const ctx = (job) =>
    job ? { jobNo: job.jobNo, customer: job.customer, wilayah: job.wilayah, strukdate: job.strukdate, category: job.category } : null;

  const missing = [];
  let totalMissing = 0;
  let ptr = 0; // index into withSeq for nearest-below lookup
  for (let n = min; n <= max; n++) {
    if (present.has(n)) continue;
    totalMissing += 1;
    if (n < lower) continue;
    // advance ptr so withSeq[ptr] is the largest seq < n
    while (ptr + 1 < withSeq.length && withSeq[ptr + 1].seq < n) ptr += 1;
    const prev = withSeq[ptr] && withSeq[ptr].seq < n ? withSeq[ptr] : null;
    const next = withSeq.find((j) => j.seq > n) || null;
    missing.push({ jobNo: 'D' + n, seq: n, prev: ctx(prev), next: ctx(next) });
  }
  // newest first, trimmed
  missing.sort((a, b) => b.seq - a.seq);
  const capped = missing.length > maxList || span > maxSpan;
  return { missing: missing.slice(0, maxList), totalMissing, min, max, span, capped };
};

/**
 * Group raw line items by NOMOR STRUK (one row per struk, bukan per item).
 * Satu struk bisa berisi banyak item dengan kategori berbeda.
 *
 * @returns {Array} [{ strukno, strukdate, struktime, customer, wilayah,
 *   jobNos[], items[], itemCount, qty, totalKotor, total, hutang }]
 */
export const groupByStruk = (rows = []) => {
  const map = new Map();
  for (const r of rows) {
    const struk = r.strukno || '(tanpa struk)';
    if (!map.has(struk)) {
      map.set(struk, {
        strukno: struk,
        strukdate: r.strukdate || '',
        struktime: r.struktime || '',
        customer: r.customername || '-',
        jobSet: new Set(),
        items: [],
        qty: 0,
        totalKotor: 0,
        total: 0,
        hutang: 0,
      });
    }
    const s = map.get(struk);
    const jobNo = extractJobNo(r.remarks);
    if (jobNo) s.jobSet.add(jobNo);
    s.items.push({
      itemname: r.itemname || '-',
      category: r.categorycode || '-',
      jobNo: jobNo || '-',
      judul: stripJobNo(r.remarks),
      qty: num(r.quantity),
      sellingprice: num(r.sellingprice),
      pdisc: num(r.pdisc),
      totalKotor: num(r.total),
      total: num(r.totalamount ?? r.total),
      hutang: num(r.hutang),
    });
    s.qty += num(r.quantity);
    s.totalKotor += num(r.total);
    s.total += num(r.totalamount ?? r.total);
    s.hutang += num(r.hutang);
  }

  return Array.from(map.values())
    .map((s) => ({
      strukno: s.strukno,
      strukdate: s.strukdate,
      struktime: s.struktime,
      customer: s.customer,
      wilayah: deriveWilayah(s.customer),
      jobNos: Array.from(s.jobSet),
      items: s.items,
      itemCount: s.items.length,
      qty: s.qty,
      totalKotor: s.totalKotor,
      total: s.total,
      hutang: s.hutang,
    }))
    .sort((a, b) => {
      if (a.strukdate !== b.strukdate) return b.strukdate.localeCompare(a.strukdate);
      return b.strukno.localeCompare(a.strukno);
    });
};

/**
 * MERGE datajob (produksi, sumber wilayah) dengan job dari POS (struk).
 *
 * @param {Array} datajobs  hasil normalizeDigitalJob (punya noJob + wilayah).
 * @param {Array} posJobs   hasil groupByJob().jobs (job yang punya struk).
 * @returns {{ matched, jobTanpaStruk, strukTanpaJob }}
 *   - jobTanpaStruk: ada di datajob, TIDAK ada struk di POS (lengkap dgn wilayah).
 *   - strukTanpaJob: ada struk di POS, nomor job-nya TIDAK ada di datajob.
 *   - matched: nomor job yang ada di kedua sumber.
 */
export const mergeDigital = (datajobs = [], posJobs = []) => {
  const posMap = new Map(posJobs.map((j) => [j.jobNo, j]));
  const dataMap = new Map(datajobs.map((d) => [d.noJob, d]));

  const jobTanpaStruk = datajobs
    .filter((d) => !posMap.has(d.noJob))
    .map((d) => ({
      jobNo: d.noJob,
      wilayah: d.wilayah,
      tanggal: d.tanggal,
      customer: d.customer,
      judul: d.judul,
      kategori: d.kategori,
      oplah: d.oplah,
    }));

  const strukTanpaJob = posJobs
    .filter((j) => !dataMap.has(j.jobNo))
    .map((j) => ({
      jobNo: j.jobNo,
      strukno: j.strukno,
      strukdate: j.strukdate,
      customer: j.customer,
      wilayah: j.wilayah,
      category: j.category,
      judul: j.judul,
      total: j.total,
    }));

  const matched = posJobs.filter((j) => dataMap.has(j.jobNo));

  return { matched, jobTanpaStruk, strukTanpaJob };
};

/**
 * High-level helper: fetch + process in one call.
 * Menggabungkan POS (struk) dengan datajob (produksi). Bila datajob tidak
 * terjangkau, fallback ke deteksi celah nomor (gap) seperti sebelumnya.
 */
export const fetchDigitalJobReport = async (startDate, endDate) => {
  const [{ count, data }, dj] = await Promise.all([
    fetchDigitalInvoiceDetails(startDate, endDate),
    fetchDigitalJobs(startDate, endDate),
  ]);

  const { jobs, unmatched } = groupByJob(data);
  const gap = detectMissingStruk(jobs);

  const totalNilai = jobs.reduce((s, j) => s + j.total, 0);
  const strukSet = new Set();
  data.forEach((r) => r.strukno && strukSet.add(r.strukno));

  // Merge dengan datajob bila tersedia.
  const merge = dj.available ? mergeDigital(dj.jobs, jobs) : null;

  // Enrich POS jobs dengan wilayah asli dari datajob (bila ada match).
  let enrichedJobs = jobs;
  if (dj.available) {
    const djMap = new Map(dj.jobs.map((d) => [d.noJob, d]));
    enrichedJobs = jobs.map((j) => {
      const djData = djMap.get(j.jobNo);
      return djData
        ? { ...j, wilayah: djData.wilayah, wilayahId: djData.wilayahId, wilayahSource: 'datajob' }
        : j;
    });
  }

  return {
    lineCount: count,
    jobs: enrichedJobs, // enriched dengan wilayah asli dari datajob bila ada
    unmatched,
    gap,
    datajob: { available: dj.available, count: dj.jobs.length, error: dj.error },
    merge,
    summary: {
      totalJob: enrichedJobs.length,
      totalStruk: strukSet.size,
      totalNilai,
      // bila datajob ada -> pakai angka merge yang akurat; jika tidak -> gap
      totalMissing: merge ? merge.jobTanpaStruk.length : gap.totalMissing,
      strukTanpaJob: merge ? merge.strukTanpaJob.length : 0,
      datajobCount: dj.jobs.length,
      unmatchedCount: unmatched.length,
    },
  };
};

/**
 * High-level helper untuk halaman PER NOMOR STRUK.
 * Mengembalikan daftar struk (sudah digabung dari level item) + ringkasan.
 * Pelunasan TIDAK di-join di sini (disimpan lokal); halaman yang menggabungkan
 * via pelunasanStore agar perhitungan sisa hutang per struk tetsap reaktif.
 * 
 * WILAYAH: Di-enrich dari datajob (gapfamily) via nomor job match.
 */
export const fetchDigitalStrukReport = async (startDate, endDate) => {
  const [{ count, data }, dj] = await Promise.all([
    fetchDigitalInvoiceDetails(startDate, endDate),
    fetchDigitalJobs(startDate, endDate),
  ]);
  
  const struks = groupByStruk(data);

  // Enrich wilayah dari datajob (bila ada match by nomor job)
  let enrichedStruks = struks;
  if (dj.available) {
    const djMap = new Map(dj.jobs.map((d) => [d.noJob, d]));
    enrichedStruks = struks.map((s) => {
      // Cari wilayah dari job pertama yang match di datajob
      for (const jobNo of s.jobNos) {
        const djData = djMap.get(jobNo);
        if (djData) {
          return {
            ...s,
            wilayah: djData.wilayah,
            wilayahId: djData.wilayahId,
            wilayahSource: 'datajob',
          };
        }
      }
      return s; // tidak ada match, pakai wilayah turunan
    });
  }

  const totalNilai = enrichedStruks.reduce((s, x) => s + x.total, 0);
  const totalHutang = enrichedStruks.reduce((s, x) => s + x.hutang, 0);
  const strukBerhutang = enrichedStruks.filter((x) => x.hutang > 0).length;

  return {
    lineCount: count,
    struks: enrichedStruks,
    datajob: { available: dj.available, count: dj.jobs.length, error: dj.error },
    summary: {
      totalStruk: enrichedStruks.length,
      totalNilai,
      totalHutang,
      strukBerhutang,
    },
  };
};
